import React, { useEffect, useState } from "react";
import { Form, Button, Row, Col } from "react-bootstrap";
import FormLabel from "../../components/Common/FormLabel";
import FormControl from "../../components/Common/FormControl";
import FormControlTextArea from "../../components/Common/FormControlTextArea";
import FormSelect from "../../components/Common/FormSelect";
import { useLocation, useNavigate } from "react-router-dom";
import { CloseCircleOutlined } from "@ant-design/icons";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import { postMultipleLeadsThunk } from "../../features/Lead/leadsSlice";
import { sendOfferThunk } from "../../redux/Slices/sentOfferSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  isValidDecimalInput,
  isValidNameWithSpecialChars,
  isValidNumberInput,
} from "../../utils/Utils";
import {
  fetchCountries,
  fetchCurrencyDetails,
} from "../../redux/Slices/locationSlice";
import { CONSTANTS } from "../../utils/Constant/Constant";
import ToastNotification from "../../components/Common/ToastNotification";
import Loader from "../../components/Common/Loader";
import appInsights from "../../AppInsights";

const AddOfferDetail = () => {
  const { trans } = useCustomTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();

  const dispatch = useDispatch();
  const MAX_FEES = 5;
  const { countries, currencies, isLoading } = useSelector(
    (state) => state.location
  );
  const { profileData } = useSelector((state) => state.profile);
  const isEditPage = !!state?.leadId;

  useEffect(() => {
    appInsights.trackPageView({
      name: "Add Offer Detail Page",
      properties: {
        coachId: profileData?.coachId,
        mode: isEditPage ? "edit" : "create",
      },
    });
  }, [profileData?.coachId, isEditPage]);

  const extractDay = (date) => {
    if (!date) return 1;
    const isValid = /^\d{4}-\d{2}-\d{2}$/.test(date);
    return isValid ? Number(date.split("-")[2]) : 1;
  };

  const [showAll, setShowAll] = useState(false);
  const [formData, setFormData] = useState({
    firstName: state?.firstName || "",
    lastName: state?.lastName || "",
    email: state?.email || "",
    phone: state?.phoneNumber || "",
    country: state?.country || "",
    language: state?.language || "",
    billingStartDate:
      state?.billingStartDate || new Date().toISOString().split("T")[0],
    subscriptionDuration: state?.subscriptionDuration || "",
    leadNotes: state?.note || "",
    currency: state?.currency || "USD",
    freePeriodEndDate: state?.freePeriodEndDate || null,
    monthlyAmount: state?.monthlyAmount || "",
    billingCycle: state?.billingCycle || "monthApart",
    customPaymentDate: state?.customPaymentDate
      ? extractDay(state?.customPaymentDate)
      : 1,
    setupFee: state?.setupFee || "",
    oneTimeFees: state?.oneTimeFees || [],
  });

  const [errors, setErrors] = useState({});
  const [toastConfig, setToastConfig] = useState(null);
  const [loading, setLoading] = useState(false);

  const subscriptionDurations = [
    { value: "", label: "Select Subscription Duration" },
    { value: "OnGoing", label: "Ongoing" },
    ...Array.from({ length: 36 }, (_, i) => ({
      value: i + 1,
      label: i === 0 ? `${i + 1} month` : `${i + 1} months`,
    })),
  ];

  const languages = [
    { value: "", label: "Select Language" },
    { value: "English", label: "English" },
    { value: "Spanish", label: "Spanish" },
    { value: "French", label: "French" },
  ];

  const billingCycles = [
    { value: "monthApart", label: trans("LeadPage.monthApart") },
    { value: "startOfTheMonth", label: trans("LeadPage.startOfTheMonth") },
    {
      value: "lastWeekDayOfMonth",
      label: trans("LeadPage.lastWeekDayOfMonth"),
    },
    { value: "custom", label: trans("LeadPage.custom") },
  ];

  useEffect(() => {
    dispatch(fetchCountries());
    dispatch(fetchCurrencyDetails());
  }, [dispatch]);

  const countriesArr = countries.map((country) => ({
    value: country.countryId,
    label: country.countryName,
  }));

  const futureDate = (date) => {
    const today = new Date();
    const todayUTC = new Date(
      Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())
    );

    const selectedDate = new Date(date);
    const selectedDateUTC = new Date(
      Date.UTC(
        selectedDate.getUTCFullYear(),
        selectedDate.getUTCMonth(),
        selectedDate.getUTCDate()
      )
    );

    return selectedDateUTC >= todayUTC;
  };

  const validateForm = () => {
    const newErrors = {};
    let formIsValid = true;

    if (!formData.firstName) {
      newErrors.firstName = trans("Common.firstNameRequired");
      formIsValid = false;
    }

    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = trans("Common.validEmailRequired");
      formIsValid = false;
    }

    if (!formData.billingStartDate) {
      newErrors.billingStartDate = trans("LeadPage.startDateRequired");
      formIsValid = false;
    } else if (!futureDate(formData.billingStartDate)) {
      newErrors.billingStartDate = trans("LeadPage.startDatePastError");
      formIsValid = false;
    }

    if (!formData.subscriptionDuration) {
      newErrors.subscriptionDuration = trans(
        "LeadPage.subscriptionDurationRequired"
      );
      formIsValid = false;
    }

    if (!formData.currency) {
      newErrors.currency = trans("Common.currencyRequired");
      formIsValid = false;
    }

    if (formData.freePeriodEndDate) {
      const freePeriodEndDate = new Date(formData.freePeriodEndDate);
      const billingStartDate = new Date(formData.billingStartDate);

      if (!futureDate(freePeriodEndDate)) {
        newErrors.freePeriodEndDate = trans(
          "LeadPage.freePeriodEndDatePastError"
        );
        formIsValid = false;
      } else if (freePeriodEndDate <= billingStartDate) {
        newErrors.freePeriodEndDate = trans(
          "LeadPage.freePeriodEndAfterBillingStartError"
        );
        formIsValid = false;
      }
    }

    if (!formData.monthlyAmount && formData.oneTimeFees.length === 0) {
      newErrors.monthlyAmount = trans("LeadPage.monthlyAmountRequired");
      formIsValid = false;
    }
    if (!formData.billingCycle) {
      newErrors.billingCycle = trans("LeadPage.billingCycleRequired");
      formIsValid = false;
    }

    if (formData.billingCycle === "custom") {
      if (!formData.customPaymentDate) {
        newErrors.customPaymentDate = trans("Custom payment date is required.");
        formIsValid = false;
      }
    }

    if (formData.oneTimeFees.length > 0) {
      formData.oneTimeFees.forEach((fee, index) => {
        const hasFee = fee.fee && fee.fee.toString().trim() !== "";
        const hasBillingDate = fee.billingDate && fee.billingDate.trim() !== "";

        // If fee is entered but billing date is not
        if (hasFee && !hasBillingDate) {
          newErrors[`oneTimeFees-${index}-billingDate`] = trans(
            "LeadPage.billingDateRequired"
          );
          formIsValid = false;
        }

        // If billing date is entered but fee is not
        if (hasBillingDate && !hasFee) {
          newErrors[`oneTimeFees-${index}-fee`] = trans(
            "LeadPage.oneTimeFeeRequired"
          );
          formIsValid = false;
        }

        // If monthly amount is not provided, both fee and billing date are required
        if (!formData.monthlyAmount) {
          if (!hasFee || isNaN(fee.fee)) {
            newErrors[`oneTimeFees-${index}-fee`] = trans(
              "LeadPage.oneTimeFeeRequired"
            );
            formIsValid = false;
          }
          if (!hasBillingDate) {
            newErrors[`oneTimeFees-${index}-billingDate`] = trans(
              "LeadPage.billingDateRequired"
            );
            formIsValid = false;
          }
        }

        // Validate billing date is in the future (only if billing date exists)
        if (hasBillingDate && !futureDate(fee.billingDate)) {
          newErrors[`oneTimeFees-${index}-billingDate`] = trans(
            "LeadPage.billingDatePastError"
          );
          formIsValid = false;
        }

        // Validate fee is a valid number (only if fee exists)
        if (hasFee && isNaN(fee.fee)) {
          newErrors[`oneTimeFees-${index}-fee`] = trans(
            "LeadPage.oneTimeFeeRequired"
          );
          formIsValid = false;
        }
      });
    }
    setErrors(newErrors);
    return formIsValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      if (!isValidNumberInput(value)) {
        return;
      }
    }

    if (name === "firstName" || name === "lastName") {
      if (!isValidNameWithSpecialChars(value)) {
        return;
      }
    }
    if (name === "customPaymentDate") {
      if (value === "") {
        // Allow empty value temporarily
        setFormData({ ...formData, [name]: value });
        return;
      }

      const numValue = parseInt(value, 10);

      if (isNaN(numValue) || numValue < 1 || numValue > 31) {
        return;
      }

      setFormData({ ...formData, [name]: numValue });
    }
    if (name === "monthlyAmount") {
      if (!isValidDecimalInput(value)) {
        return;
      } else if (value === "") {
        // Allow empty value temporarily
        setFormData({ ...formData, [name]: value });
        return;
      }

      const numValue = parseFloat(value);

      if (
        isNaN(numValue) ||
        numValue < 1 ||
        (value.includes(".") && value.split(".")[1].length > 2)
      ) {
        return;
      }
    }
    if (name === "setupFee") {
      if (!isValidDecimalInput(value)) {
        return;
      }
    }

    if (name === "leadNotes" && value.length > 255) {
      return;
    }

    if (name === "currency") {
      if (!value) {
        setFormData({ ...formData, [name]: value });
        return;
      }
    }

    if (name === "country") {
      const selectedCountry = countriesArr.find(
        (country) => String(country.countryId) === String(value)
      );

      setFormData((prev) => ({
        ...prev,
        country: selectedCountry ? selectedCountry.countryId : value,
      }));

      setErrors((prev) => ({ ...prev, country: "" }));

      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  const handleOneTimeFeeChange = (index, field, value) => {
    if (
      field === "fee" &&
      (parseFloat(value) < 0 ||
        (value.includes(".") && value.split(".")[1]?.length > 2))
    ) {
      return;
    }
    const updatedFees = formData.oneTimeFees.map((fee, idx) =>
      idx === index ? { ...fee, [field]: value } : fee
    );
    setFormData({ ...formData, oneTimeFees: updatedFees });
  };

  const addOneTimeFee = () => {
    if (formData.oneTimeFees.length < MAX_FEES) {
      const updatedFees = [
        ...formData.oneTimeFees,
        { fee: "", billingDate: "" },
      ];
      setFormData({ ...formData, oneTimeFees: updatedFees });
    }
  };

  const removeOneTimeFee = (index) => {
    const updatedFees = formData.oneTimeFees.filter((_, idx) => idx !== index);
    setFormData({ ...formData, oneTimeFees: updatedFees });

    const updatedErrors = { ...errors };
    delete updatedErrors[`oneTimeFees-${index}-fee`];
    delete updatedErrors[`oneTimeFees-${index}-billingDate`];

    const reIndexedErrors = {};
    Object.keys(updatedErrors).forEach((key) => {
      if (key.startsWith("oneTimeFees-")) {
        // eslint-disable-next-line no-unused-vars
        const [_, idx, field] = key.split("-");
        const newIndex = idx > index ? idx - 1 : idx;
        reIndexedErrors[`oneTimeFees-${newIndex}-${field}`] =
          updatedErrors[key];
      } else {
        reIndexedErrors[key] = updatedErrors[key];
      }
    });

    setErrors(reIndexedErrors);
  };

  const changeShow = () => {
    setShowAll(!showAll);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    appInsights.trackEvent({
      name: "Add_offer_detail_page: Submit Offer Clicked",
      properties: {
        coachId: profileData?.coachId,
        mode: isEditPage ? "edit" : "create",
      },
    });

    if (!validateForm()) {
      console.log("Validation failed, API call blocked.");
      return;
    }

    setLoading(true);

    try {
      const coachId = profileData?.coachId;
      let leadId = state?.leadId;

      let customPaymentDateString = null;
      if (formData.billingCycle === "custom" && formData.customPaymentDate) {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1;
        const day = formData.customPaymentDate;

        customPaymentDateString = `${currentYear}-${String(
          currentMonth
        ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      }

      //CASE 1: If Coming from Landing Page, Create Lead First
      if (!isEditPage) {
        const leadPayload = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: formData.phone,
          language: formData.language,
          country: formData.country,
          note: formData.leadNotes,
          status: "New Lead",
          isLeadFromSite: false,
          createdBy: "",
          updatedBy: "",
        };

        const leadResponse = await dispatch(
          postMultipleLeadsThunk({ coachId, leads: [leadPayload] })
        ).unwrap();

        if (leadResponse.status !== "Success" || !leadResponse.leadIds.length) {
          throw new Error("Lead creation failed");
        }

        leadId = leadResponse.leadIds[0];
        // setLeadIdNew(leadId);
      }

      // Filter out incomplete or empty one-time fees
      const validOneTimePayments = formData.oneTimeFees
        .filter((fee) => fee.fee && fee.billingDate)
        .map((fee) => ({
          amount: parseFloat(fee.fee),
          paymentDate: fee.billingDate,
          isReactivate: false,
          clientId: null,
        }));
      const offerPayload = {
        leadId: leadId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        language: formData.language,
        country: formData.country,
        billingStartDate: formData.billingStartDate,
        subscriptionDuration: formData.subscriptionDuration,
        leadNotes: formData.leadNotes,
        currency: formData.currency,
        freePeriodEndDate: formData.freePeriodEndDate,
        monthlyAmount: parseFloat(formData.monthlyAmount),
        billingCycle: formData.billingCycle,
        customPaymentDate:
          formData.billingCycle === "custom" ? customPaymentDateString : null,
        setupFee: parseFloat(formData.setupFee || 0),
        oneTimePayments: validOneTimePayments,
      };

      const offerResponse = await dispatch(
        sendOfferThunk({ coachId, offerPayload })
      ).unwrap();

      appInsights.trackEvent({
        name: "Add_offer_detail_page: Offer Sent Successfully",
        properties: {
          coachId,
          leadId: state?.leadId,
        },
      });

      if (offerResponse.status === "Failed") {
        setToastConfig({
          type: CONSTANTS.TOAST_ERROR,
          message: offerResponse.message,
          heading: trans("Common.failed"),
        });
        setTimeout(() => {
          setToastConfig(null);
        }, 3000);
      } else {
        navigate("/EmailWriter", { state: offerResponse });
      }
    } catch (error) {
      appInsights.trackException({
        exception: error,
        severityLevel: 2,
        properties: {
          name: "Add_offer_detail_page: Offer Send Failed",
          error: error?.message,
        },
      });

      setToastConfig({
        type: CONSTANTS.TOAST_ERROR,
        message: error.message || trans("Common.somethingWentWrong"),
        heading: trans("Common.failed"),
      });
      setTimeout(() => {
        setToastConfig(null);
      }, 3000);
      console.error("Error in submitting offer:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className=" formContainer">
      <div className="createLeadHeading">
        <div className="cancelButton">
          <Button
            variant="outline-primary"
            size="sm"
            className="cancel-button"
            onClick={() => navigate("/leads")}
          >
            <i className="bi bi-chevron-left"></i> {trans("Common.back")}
          </Button>
          <h4 className="ms-3 mt-2">{trans("LeadPage.addOfferDetails")}</h4>
        </div>

        <Button
          variant="primary"
          className="continueButton"
          onClick={handleSubmit}
        >
          {trans("Common.continue")}
        </Button>
      </div>

      <Form
        onSubmit={handleSubmit}
        className="templateWrapper px-4 py-4"
        autoComplete="off"
      >
        <Row>
          <h4 className="form_heading">{trans("LeadPage.details")}</h4>
        </Row>
        <Row>
          <h6 className="pb-2">{trans("LeadPage.personalInformation")}</h6>
        </Row>
        {/* First Row */}
        <Row className="mb-3">
          <Col xs={12} md={6} lg={3}>
            <Form.Group controlId="formFirstName">
              <FormLabel label={trans("Common.firstName")} required />
              <FormControl
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                isInvalid={!!errors.firstName}
                feedback={errors.firstName}
                placeholder={trans("Common.enterFirstName")}
                autoComplete="offerdetails-firstName"
              />
            </Form.Group>
          </Col>

          <Col xs={12} md={6} lg={3}>
            <Form.Group controlId="formLastName">
              <FormLabel label={trans("Common.lastName")} />
              <FormControl
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder={trans("Common.enterLastName")}
                autoComplete="offerdetails-lastName"
              />
            </Form.Group>
          </Col>

          <Col xs={12} md={6} lg={3}>
            <Form.Group controlId="formPhone">
              <FormLabel label={trans("Common.phoneNumber")} />
              <FormControl
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder={trans("Common.enterPhoneNumber")}
                autoComplete="offerdetails-phone"
              />
            </Form.Group>
          </Col>

          <Col xs={12} md={6} lg={3}>
            <Form.Group controlId="formEmail">
              <FormLabel label={trans("Common.email")} required />
              <FormControl
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                isInvalid={!!errors.email}
                feedback={errors.email}
                placeholder={trans("Common.enterEmail")}
              />
            </Form.Group>
          </Col>
        </Row>
        {/* Second Row */}
        <Row className="mb-3">
          <Col xs={12} md={6} lg={3}>
            <FormLabel label="Subscription Start Date" required />
            <FormControl
              type="date"
              name="billingStartDate"
              value={formData.billingStartDate}
              onChange={handleChange}
              isInvalid={!!errors.billingStartDate}
              feedback={errors.billingStartDate}
              placeholder={trans("LeadPage.enterStartDate")}
              autoComplete="offerdetails-billingStartDate"
            />
          </Col>

          <Col xs={12} md={6} lg={3}>
            <FormLabel label="Subscription Duration" required />
            <FormSelect
              name="subscriptionDuration"
              value={formData.subscriptionDuration}
              onChange={handleChange}
              options={subscriptionDurations}
              isInvalid={!!errors.subscriptionDuration}
              feedback={errors.subscriptionDuration}
              placeholder={trans("LeadPage.selectSubscriptionDuration")}
              autoComplete="offerdetails-subscriptionDuration"
            />
          </Col>

          <Col xs={12} md={6} lg={3}>
            <FormLabel label={trans("LeadPage.billingCycle")} required />
            <FormSelect
              name="billingCycle"
              value={formData.billingCycle}
              onChange={handleChange}
              options={billingCycles}
              isInvalid={!!errors.billingCycle}
              feedback={errors.billingCycle}
              placeholder={trans("LeadPage.selectBillingCycle")}
              autoComplete="offerdetails-billingCycle"
            />
          </Col>
          {formData.billingCycle === "custom" && (
            <Col xs={12} md={6} lg={3}>
              <FormLabel label="Custom Payment Day" required />
              <FormControl
                type="number"
                name="customPaymentDate"
                value={formData.customPaymentDate}
                onChange={handleChange}
                isInvalid={!!errors.customPaymentDate}
                feedback={errors.customPaymentDate}
                placeholder={trans("LeadPage.enterCustomPaymentDate")}
                autoComplete="offerdetails-customPaymentDate"
              />
            </Col>
          )}
        </Row>

        {/* Third Row */}
        <Row className="mb-3">
          <Col xs={12} md={6}>
            <Form.Group controlId="formLeadNotes">
              <FormLabel label={trans("LeadPage.leadNotes")} />
              <FormControlTextArea
                name="leadNotes"
                value={formData.leadNotes}
                onChange={handleChange}
                rows={3}
                placeholder={trans("LeadPage.enterLeadNotes")}
                autoComplete="offerdetails-leadNotes"
              />
            </Form.Group>
          </Col>
          <Col>
            <div
              className="mt-5 text-primary text-decoration-underline cursor-pointer"
              onClick={changeShow}
            >
              <h6>
                {showAll
                  ? trans("ClientMealPlan.showLess")
                  : trans("ClientMealPlan.showMore")}
              </h6>
            </div>
          </Col>
        </Row>
        {/* Fourth Row */}
        <Row className="mb-3">
          {showAll && (
            <div>
              <Row>
                <Col xs={12} md={6} lg={3}>
                  <Form.Group controlId="formFreePeriodEndDate">
                    <FormLabel label={trans("LeadPage.freePeriodEndOn")} />
                    <FormControl
                      type="date"
                      name="freePeriodEndDate"
                      value={formData.freePeriodEndDate}
                      onChange={handleChange}
                      isInvalid={!!errors.freePeriodEndDate}
                      feedback={errors.freePeriodEndDate}
                      placeholder={trans("LeadPage.enterFreePeriodEndDate")}
                      autoComplete="offerdetails-freePeriodEndDate"
                    />
                  </Form.Group>
                </Col>

                <Col xs={12} md={6} lg={3}>
                  <FormSelect
                    label={trans("Common.language")}
                    name="language"
                    value={formData.language}
                    onChange={handleChange}
                    options={languages}
                    placeholder={trans("Common.selectLanguage")}
                    autoComplete="offerdetails-language"
                  />
                </Col>

                <Col xs={12} md={6} lg={3}>
                  <FormSelect
                    label={trans("Common.country")}
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    options={[
                      { value: "", label: trans("Common.selectCountry") },
                      ...countriesArr,
                    ]}
                    placeholder={trans("Common.selectCountry")}
                    autoComplete="offerdetails-country"
                    loading={isLoading}
                  />
                </Col>
              </Row>
            </div>
          )}
        </Row>
        {/* Fifth Row */}
        <Row className="mb-3">
          <Col xs={12} md={6} lg={3}>
            <FormSelect
              label={trans("Common.currency")}
              name="currency"
              labelRequired={true}
              value={formData.currency}
              onChange={handleChange}
              options={[
                ...currencies.map((currency) => ({
                  value: currency,
                  label: currency,
                })),
              ]}
              isInvalid={!!errors.currency}
              feedback={errors.currency}
              autoComplete="offerdetails-currency"
              loading={isLoading}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <Form.Group controlId="formMonthlyAmount">
              <FormLabel
                label={trans("LeadPage.monthlyAmount")}
                required={formData.oneTimeFees.length > 0 ? false : true}
              />
              <FormControl
                type="text"
                name="monthlyAmount"
                value={formData.monthlyAmount}
                onChange={handleChange}
                isInvalid={!!errors.monthlyAmount}
                feedback={errors.monthlyAmount}
                placeholder={trans("LeadPage.enterMonthlyAmount")}
                autoComplete="offerdetails-monthlyAmount"
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6} lg={3}>
            <Form.Group controlId="formSetupFee">
              <FormLabel label={trans("LeadPage.setupFee")} />
              <FormControl
                type="text"
                name="setupFee"
                value={formData.setupFee}
                onChange={handleChange}
                placeholder={trans("LeadPage.enterSetupFee")}
                autoComplete="offerdetails-setupFee"
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6} lg={3}>
            <Button
              variant="link"
              className="text-primary text-decoration-none p-0 ms-2 mt-6"
              onClick={addOneTimeFee}
              disabled={formData?.oneTimeFees?.length >= MAX_FEES}
            >
              + {trans("LeadPage.addOneTimePayment")}
            </Button>
          </Col>
        </Row>
        {formData?.oneTimeFees?.map((fee, index) => (
          <Row key={index} className="mb-3 align-items-center">
            <Col xs={12} md={6} lg={3}>
              <Form.Group controlId={`formOneTimeFee-${index}`}>
                <FormLabel
                  label={trans("LeadPage.oneTimePayment")}
                  required={!formData.monthlyAmount}
                />
                <FormControl
                  type="number"
                  name={`oneTimeFees[${index}].fee`}
                  value={fee.fee}
                  isInvalid={!!errors[`oneTimeFees-${index}-fee`]}
                  feedback={errors[`oneTimeFees-${index}-fee`]}
                  onChange={(e) =>
                    handleOneTimeFeeChange(index, "fee", e.target.value)
                  }
                  placeholder={trans("LeadPage.enterOneTimeFee")}
                  autoComplete="offerdetails-oneTimeFee"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6} lg={3}>
              <Form.Group controlId={`formBillingDate-${index}`}>
                <FormLabel
                  label={trans("LeadPage.billingDate")}
                  required={!formData.monthlyAmount}
                />
                <FormControl
                  type="date"
                  name={`oneTimeFees[${index}].billingDate`}
                  value={fee.billingDate}
                  isInvalid={!!errors[`oneTimeFees-${index}-billingDate`]}
                  feedback={errors[`oneTimeFees-${index}-billingDate`]}
                  onChange={(e) =>
                    handleOneTimeFeeChange(index, "billingDate", e.target.value)
                  }
                  placeholder={trans("LeadPage.enterBillingDate")}
                  autoComplete="offerdetails-billingDate"
                />
              </Form.Group>
            </Col>
            <Col xs="auto">
              <Button
                variant="link"
                className="text-danger p-0"
                onClick={() => removeOneTimeFee(index)}
              >
                <CloseCircleOutlined className="close-circle-style" />
              </Button>
            </Col>
            {formData.billingCycle === "Custom" && (
              <Col xs={12} md={6} lg={3}>
                <FormLabel label="Custom Payment Day" required />
                <FormControl
                  type="number"
                  name="customPaymentDate"
                  value={formData.customPaymentDate}
                  onChange={handleChange}
                  isInvalid={!!errors.customPaymentDate}
                  feedback={errors.customPaymentDate}
                  autoComplete="offerdetails-customPaymentDate"
                />
              </Col>
            )}
          </Row>
        ))}
        <Row className="mt-3 justify-content-end">
          <Button
            variant="primary"
            style={{ width: "100px" }}
            className="continueButton"
            onClick={handleSubmit}
          >
            {trans("Common.continue")}
          </Button>
        </Row>
      </Form>
      {toastConfig && (
        <ToastNotification
          type={toastConfig.type}
          message={toastConfig.message}
          heading={toastConfig.heading}
        />
      )}
    </div>
  );
};

export default AddOfferDetail;
